import { useMemo, useState } from "react";
import { InputAdornment, TextField, type TextFieldProps } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

/**
 * The search box that sits above a master's grid, plus the filter behind it.
 *
 * The masters that pull their whole list in one call (a few hundred rows at most) filter in the
 * browser rather than round-tripping per keystroke — `useListSearch` is that filter. Masters served
 * page-by-page from the API keep their own server-side `search` param instead; this is only for the
 * client-side ones.
 */
export function ListSearchField({ value, onChange, placeholder = "Search", ...rest }: {
  value: string;
  onChange: (v: string) => void;
} & Omit<TextFieldProps, "value" | "onChange">) {
  return (
    <TextField
      placeholder={placeholder}
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
      }}
      {...rest}
    />
  );
}

/**
 * Filters `rows` against the typed terms. Terms are AND-ed, so "sterile tray" narrows the way a
 * typist expects instead of matching only that exact adjacent phrase.
 *
 * `textOf` should return what the row shows on screen — including labels resolved from another
 * query, which the row object itself doesn't carry.
 */
export function useListSearch<T>(rows: T[], search: string, textOf: (row: T) => string): T[] {
  return useMemo(() => {
    const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return rows;
    return rows.filter((row) => {
      const haystack = textOf(row).toLowerCase();
      return terms.every((t) => haystack.includes(t));
    });
    // textOf is left out of the deps on purpose: callers pass an inline arrow that closes over
    // lookup maps rebuilt every render, so depending on it would defeat the memo entirely. A stale
    // result isn't possible in practice — the label it reads comes from the same query as `rows`.
  }, [rows, search]);
}

/** Convenience for the common case: local `search` state + the filtered rows. */
export function useSearchableRows<T>(rows: T[], textOf: (row: T) => string) {
  const [search, setSearch] = useState("");
  return { search, setSearch, rows: useListSearch(rows, search, textOf) };
}
